import { useEffect, useRef } from "react";
import { WeatherLayerOption } from "../../types/map/types";

interface UseWeatherLayersProps {
  map: google.maps.Map | null;
  weatherLayers: WeatherLayerOption[];
}

const API_KEY = process.env.REACT_APP_WEATHER_API_KEY;

export const useWeatherLayers = ({
  map,
  weatherLayers,
}: UseWeatherLayersProps) => {
  const activeLayerRef = useRef<google.maps.ImageMapType | null>(null);
  const previousActiveTypeRef = useRef<string | null>(null);

  useEffect(() => {
    if (!map) return;

    const activeLayer = weatherLayers.find((layer) => layer.isVisible);
    const activeType = activeLayer?.type;

    // If no layer is active, remove the overlay
    if (!activeType) {
      if (activeLayerRef.current) {
        // Find and remove only our weather overlay
        const overlayCount = map.overlayMapTypes.getLength();
        for (let i = overlayCount - 1; i >= 0; i--) {
          const overlay = map.overlayMapTypes.getAt(i);
          if (overlay === activeLayerRef.current) {
            map.overlayMapTypes.removeAt(i);
            break;
          }
        }
        activeLayerRef.current = null;
      }
      previousActiveTypeRef.current = null;
      return;
    }

    // Only recreate layer if the type changed
    if (previousActiveTypeRef.current !== activeType) {
      // Remove old layer if it exists
      if (activeLayerRef.current) {
        const overlayCount = map.overlayMapTypes.getLength();
        for (let i = overlayCount - 1; i >= 0; i--) {
          const overlay = map.overlayMapTypes.getAt(i);
          if (overlay === activeLayerRef.current) {
            map.overlayMapTypes.removeAt(i);
            break;
          }
        }
      }

      // Create new layer - simple, no timestamps, no animation
      const layerName =
        activeType === "precipitation"
          ? "precipitation_new"
          : activeType === "clouds"
          ? "clouds_new"
          : activeType === "temperature"
          ? "temp_new"
          : activeType === "wind"
          ? "wind_new"
          : "pressure_new";

      const weatherLayer = new google.maps.ImageMapType({
        getTileUrl: (coord, zoom) => {
          return `https://tile.openweathermap.org/map/${layerName}/${zoom}/${coord.x}/${coord.y}.png?appid=${API_KEY}`;
        },
        tileSize: new google.maps.Size(256, 256),
        name: layerName,
        maxZoom: 18,
        opacity: 0.8,
      });

      map.overlayMapTypes.push(weatherLayer);
      activeLayerRef.current = weatherLayer;
      previousActiveTypeRef.current = activeType;
    }
  }, [map, weatherLayers]);
};
